import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import Common from '@/libs/common';
import type { Group } from '@/@types/group';
import api from '@/api';
import { isHttpError } from '@/libs/api-helper';
import type { ExtendedWindow } from '@/@types/global';
import { useAppStore } from './app';
import { useAlertsStore } from './alerts';
import { useDevicesStore } from './devices';
import { useFavoriteGroupsStore } from './favorite-groups';
import { useGtfsStore } from './gtfs';
import { useOnDemandStore } from './on-demand';
import { useRailStore } from './rail';
import { useTripUpdatesStore } from './trip-updates';
import { datadogLogs } from '@datadog/browser-logs';
import { AppRoute } from '@/libs/routing';
import { useRouter } from 'vue-router';

declare let window: ExtendedWindow;

export const useGroupStore = defineStore('group', () => {
  const appStore = useAppStore();
  const alertStore = useAlertsStore();
  const devicesStore = useDevicesStore();
  const favoriteGroupsStore = useFavoriteGroupsStore();
  const router = useRouter();

  const gtfsStore = useGtfsStore();
  const onDemandStore = useOnDemandStore();
  const railStore = useRailStore();
  const tripUpdatesStore = useTripUpdatesStore();
  const GROUP_RETRY_INTERVAL = 15000;

  // state
  const group = ref<Group | null>(Common.safeLSParse('group') || null);
  // Set synchronously by connectGroup: `group` stays null until the API answers, so the route
  // watcher cannot tell that this network is already being connected.
  const requestedGroupId = ref<string | null>(null);

  //functions
  function setGroup(groupFromApi: Group | null) {
    if (groupFromApi === null) {
      group.value = null;
      return;
    }
    const groupParsed: Group = {
      group_id: groupFromApi.group_id,
      name: groupFromApi.name,
      color: groupFromApi.color,
      logo_url: groupFromApi.logo_url || undefined,
      current_file: groupFromApi.current_file,
      contact: groupFromApi.contact || {},
      tz: groupFromApi.tz || 'Europe/Paris',
      private_routes: groupFromApi.private_routes || undefined,
    };
    group.value = groupParsed;
    localStorage.setItem('group', JSON.stringify(groupParsed));
    // Opening a network by any path (picker, link, notification) makes it a favorite.
    favoriteGroupsStore.add(groupParsed);
    favoriteGroupsStore.refresh(groupParsed);
    favoriteGroupsStore.markUsed(groupParsed.group_id);
  }

  // Bumped on every connection so that a retry loop for a network the user has
  // since left stops.
  let connectRun = 0;

  /**
   * Offline, the saved network must not be dropped for the network picker: the
   * call is retried until the connection comes back. Only an HTTP error means
   * the network is invalid. Resolves null when superseded by another connection.
   */
  async function fetchGroup(groupId: string, run: number): Promise<Group | null> {
    for (;;) {
      try {
        const group = await api.getGroup(groupId);
        return run === connectRun ? group : null;
      } catch (e) {
        if (isHttpError(e)) {
          if (run !== connectRun) return null;
          throw e;
        }
      }
      await new Promise(resolve => setTimeout(resolve, GROUP_RETRY_INTERVAL));
      if (run !== connectRun) return null;
    }
  }

  async function connectGroup(groupId: string) {
    requestedGroupId.value = groupId || null;
    api.sendAppEvent(groupId);
    // Handle count session by groupId in custom report Matomo
    setTimeout(() => {
      if (window._paq) {
        // Set for the custom report "group" (id = 1)
        window._paq.push(['setCustomDimension', 1, groupId]);
      }
    }, 2000);

    setGroup(null);

    if (!groupId) return;

    appStore.setLoading({ group: true });
    const run = ++connectRun;

    try {
      const group = await fetchGroup(groupId, run);
      if (!group) return;
      alertStore.getAlerts(groupId);
      railStore.loadStationLinks(groupId);
      onDemandStore.loadRoutes(groupId);
      devicesStore.loadVehicles();
      setGroup(group);
    } catch (e) {
      if (!isHttpError(e)) console.error('Unexpected error while connecting to the network', e);
      // A superseded cache read leaves the gtfs flag raised for a network that never loads.
      appStore.setLoading({ group: false, gtfs: false });
      if (run === connectRun) requestedGroupId.value = null;
      router.push({ name: AppRoute.SELECT_GROUP });
      return;
    }

    const reloadReason = gtfsApiReloadReason();
    if (reloadReason) {
      // Expected path, logged below warn so that it is not forwarded to Datadog.
      console.info(`${reloadReason}, load gtfs data from API`);
      gtfsStore.loadCurrentGtfsFromAPI();
    } else {
      try {
        await gtfsStore.loadGtfsFromIDB();
        // Another connection started meanwhile: loadFavorites would match the new network's
        // favorites against this network's stops and rewrite them.
        if (run !== connectRun) return;

        appStore.loadFavorites();
      } catch (e) {
        if (run !== connectRun) return;
        console.warn(
          'No Gtfs found for this GtfsId in IndexedDB or error while fetching from IDB, Load gtfs data from API',
          e,
        );
        gtfsStore.loadCurrentGtfsFromAPI();
      }
    }

    // A newer connection owns the loading state now.
    if (run === connectRun) appStore.setLoading({ group: false });
  }

  /** Why the cached GTFS in IndexedDB must be skipped, or null when it can be used. */
  function gtfsApiReloadReason(): string | null {
    // #region temporary bugfix
    // This part of code is aimed to fix a bug with anterior versions. It forces to reload gtfs data in indexedDB
    // Previous bug made gtfs stored in indexedDB already filtered with private_routes, resulting in missing data when private_routes changed.
    // All details can be found in this issue: https://gitlab.com/pysae/info/-/issues/198
    // Remove this code in 1 year, arround 01/2027
    const hasAlreadyReloadedGtfsBug198 = Common.safeLSParse('hasAlreadyReloadedGtfsBug198');
    if (!hasAlreadyReloadedGtfsBug198) {
      localStorage.setItem('hasAlreadyReloadedGtfsBug198', JSON.stringify(true));
      return 'Force reload of gtfs to fix potential missing data in indexedDB (bug #198)';
    }
    // #endregion temporary bugfix

    const savedGtfsId = localStorage.getItem('gtfsData.gtfsId');
    if (group.value?.current_file !== savedGtfsId) return 'gtfsId changed';

    return null;
  }

  // Watch `group` change to start/stop the trip updates polling
  watch(
    () => group.value?.group_id,
    groupId => {
      if (groupId) {
        tripUpdatesStore.startTripUpdatesPolling();
        datadogLogs.setGlobalContextProperty('group_id', groupId);
      } else {
        tripUpdatesStore.stopTripUpdatesPolling();
        datadogLogs.removeGlobalContextProperty('group_id');
      }
    },
    { immediate: true },
  );

  return { group, requestedGroupId, connectGroup };
});
